import { ArrowDownIcon, ArrowLeftIcon, ArrowUpIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setGlobalConfig } from 'actions'
import cloneDeep from 'clone-deep'
import { OptionModal } from 'components/Modals/OptionModal'
import { type InputType, SettingKey } from 'config'
import { getLoanInputFieldOptions, getLoanInputFields } from 'config/loan.input.fields.constants'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { submitCondition } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button, Checkbox, Toggle } from 'stories/components'
import { capitalizeFirstLetter, confirm, InputConvert, InputValidate, removeComma } from 'utils'
import { getOptionValues, IOptionType } from 'utils/general'
import { RenderInput } from 'utils/RenderInput'

import { conditionInputs } from '../constant'
import { ILoanStructureConditionFieldOriginOptions } from '../LoanStructureConditions/types'
import { defaultChecklistInputs } from './constant'
import type { IChecklistItem, IChecklistVisibilityOption, ICondition, IConditionTemplate } from './types'

export function ConditionDetails(props: {
  templates: IConditionTemplate[]
  defaults: ICondition | null
  isDuplicated: boolean
  lastUpdatedAt: number
  onBack: Function
  onComplete: Function
  categories: string[]
}) {
  const dispatch = useDispatch()

  const [conditionTypeOptions, documentCategoryOptions] = useSelector((state: any) => {
    return [
      state.globalConfigReducer[GlobalConfigType.ConditionTypes],
      state.globalConfigReducer[GlobalConfigType.DocumentCategory],
    ]
  })

  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [selectedTemplates, setSelectedTemplates] = useState<Array<number> | null>(null)
  const { templates = [], defaults = null, isDuplicated, lastUpdatedAt } = props
  const [isForActiveLoan, setForActiveLoan] = useState(false)
  const [typeSettingModal, setTypeModal] = useState<SettingKey | null>(null)
  const [checklist, setChecklist] = useState<IChecklistItem[]>(defaults?.checklist || [])
  const [error, setError] = useState(false)

  const updateInputs = (inputs: Record<string, InputType>) => {
    inputs.reviewActionLabel.disabled = !inputs.reviewActionStatus.value
  }

  useEffect(() => {
    if (!defaults) {
      setInputs(conditionInputs(props.categories))
      setSelectedTemplates([])
      return
    }
    const newStats = cloneDeep(conditionInputs(props.categories)) as Record<string, any>
    for (const key in newStats) {
      newStats[key].value = (defaults as any)[key]
    }
    if (isDuplicated) newStats.name.value = ''
    updateInputs(newStats)
    setInputs(newStats)

    const newTemplates: Array<number> = []
    templates.forEach((template) => {
      if (template.conditions.indexOf(defaults.no) != -1) newTemplates.push(template.no)
    })
    setSelectedTemplates(newTemplates)
  }, [defaults, lastUpdatedAt, templates, isDuplicated])

  const onBack = () => {
    props.onBack()
  }

  const onChange = (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    updateInputs(newInputs)
    if (key == 'reviewActionStatus' && value == true && !newInputs.reviewActionLabel.value)
      newInputs.reviewActionLabel.value = capitalizeFirstLetter(newInputs.name.value.toLowerCase())

    setInputs(newInputs)
  }

  const changeTempConds = (no: number, value: boolean) => {
    if (!selectedTemplates) return
    const newValues = cloneDeep(selectedTemplates)
    const index = newValues.indexOf(no)
    if (value && index == -1) newValues.push(no)
    else if (!value && index != -1) newValues.splice(index, 1)
    setSelectedTemplates(newValues)
  }

  const onSubmit = async () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true

      if (!disabled && value !== undefined) data[key] = value
    }
    checklist.map((config: IChecklistItem) => {
      config.visibleLogic?.map((andLogic: IChecklistVisibilityOption[]) => {
        andLogic.map((logic: IChecklistVisibilityOption) => {
          if (!logic.fieldOrigin || logic.fieldKey === '' || logic.value === '') hasError = true
        })
      })
    })
    if (hasError) {
      setError(hasError)
      setInputs(newStats)
      return
    }

    if (isForActiveLoan) {
      const isUpdateLoans = await confirm('Do you want to update all active Loans?')
      data.isUpdateLoans = isUpdateLoans
    }

    setLoading(true)

    data.templates = selectedTemplates

    const id = !defaults || isDuplicated ? 0 : defaults.id
    if (isDuplicated && defaults) {
      delete (defaults as any).id
      delete (defaults as any).no
    }

    data.checklist = checklist
    submitCondition(id, data)
      .then(() => {
        if (id == 0) toast('New condition is added.', { type: 'success' })
        else toast(`Condition No ${defaults?.displayNo} is updated.`, { type: 'success' })

        props.onComplete()
        onBack()
      })
      .catch(() => setLoading(false))
  }

  const checklistOptions = useMemo(() => {
    const rtl: Record<string, string> = {}
    checklist.map((item) => {
      rtl[item.key] = item.title
    })
    return rtl
  }, [checklist])

  const checklistInputs = useMemo(() => {
    const rtl: Record<string, any> = {}
    checklist.map((item) => {
      rtl[item.key] = {
        inputType: item.inputType,
        title: item.title,
      }
    })
    return rtl
  }, [checklist])

  const onAddChecklist = () => {
    const temp = cloneDeep(checklist)
    temp.push({
      inputType: '',
      title: '',
      key: `${Date.now()}`,
      eligibleValue: '',
      checkIneligible: false,
      visibleLogic: [],
    })
    setChecklist(temp)
  }

  const onRemoveChecklist = (index: number) => {
    const temp = cloneDeep(checklist)
    temp.splice(index, 1)
    setChecklist(temp)
  }

  const onMoveChecklist = (index: number, dir: 1 | -1) => {
    if (index + dir < 0 || index + dir >= checklist.length) return

    const temp = cloneDeep(checklist)
    const curItem = temp[index]
    temp[index] = temp[index + dir]
    temp[index + dir] = curItem
    setChecklist(temp)
  }

  const onChangeChecklist = (index: number, key: string, value: any) => {
    const temp: any = cloneDeep(checklist)
    temp[index][key] = value
    setChecklist(temp)
  }

  const renderVisibleLogicOR = (control: IChecklistItem, index: number) => {
    const onAddVisibleLogic = (orIndex: number) => {
      const temp = cloneDeep(checklist)
      const newConfig = temp[index]

      if (!newConfig.visibleLogic) newConfig.visibleLogic = []
      if (orIndex == -1) newConfig.visibleLogic.push([])
      else
        newConfig.visibleLogic[orIndex].push({
          fieldOrigin: 'ChecklistFields',
          fieldKey: '',
          value: '',
        } as any)
      setChecklist(temp)
    }

    const onRemoveVisibleLogic = (orIndex: number, andIndex: number = -1) => {
      const temp = cloneDeep(checklist)
      const newConfig = temp[index]

      if (!newConfig.visibleLogic) return

      if (andIndex == -1) newConfig.visibleLogic.splice(orIndex, 1)
      else newConfig.visibleLogic[orIndex].splice(andIndex, 1)

      setChecklist(temp)
    }

    const renderVisibleLogic = (option: IChecklistVisibilityOption, orIndex: number, andIndex: number) => {
      const onUpdateVisibleLogic = (key: string, value: any, input: Record<string, any>) => {
        const temp = cloneDeep(checklist)
        const newConfig = temp[index]

        if (['number', 'thousandSep', 'thousandSepNoDecimal'].includes(input.type)) value = removeComma(value)
        ;(newConfig.visibleLogic![orIndex][andIndex] as any)[key] = value
        if (['fieldOrigin', 'fieldKey'].includes(key)) {
          ;(newConfig.visibleLogic![orIndex][andIndex] as any).value = ''
          if (key === 'fieldOrigin') (newConfig.visibleLogic![orIndex][andIndex] as any).fieldKey = ''
        }
        setChecklist(temp)
      }

      const { fieldOrigin, fieldKey, value } = option
      const inputs: Record<string, InputType> = {}

      const fieldOriginOptions = {
        ...ILoanStructureConditionFieldOriginOptions,
        ChecklistFields: 'Checklist Fields',
      }

      inputs.fieldOrigin = {
        inputType: 'select',
        title: 'Page Origin',
        options: fieldOriginOptions,
        hasDefaultOption: true,
        value: fieldOrigin || 'ChecklistFields',
        required: true,
        sort: false,
        span: 1,
      }

      const currentFieldOrigin = fieldOrigin || 'ChecklistFields'

      if (currentFieldOrigin === 'ChecklistFields') {
        inputs.fieldKey = {
          inputType: 'select',
          title: 'Select Field',
          options: checklistOptions,
          hasDefaultOption: true,
          value: fieldKey,
          required: true,
          sort: true,
          span: 1,
        }

        if (fieldKey) {
          inputs.value = {
            ...(checklistInputs[fieldKey] || {}),
            value: value as any,
            span: 'full',
          }
        }
      } else if (currentFieldOrigin) {
        const fieldOptions = getLoanInputFieldOptions()
        const inputFields = getLoanInputFields()

        inputs.fieldKey = {
          inputType: 'select',
          title: 'Select Field',
          options: (fieldOptions as any)[currentFieldOrigin] || {},
          hasDefaultOption: true,
          value: fieldKey,
          required: true,
          sort: true,
          span: 1,
        }

        if (fieldKey && (inputFields as any)[currentFieldOrigin]?.[fieldKey]) {
          inputs.value = {
            ...(inputFields as any)[currentFieldOrigin][fieldKey],
            value: value as any,
            span: 'full',
          }
        }
      }

      return (
        <div className="pl-4">
          <div className="flex justify-between">
            <p className="text-sm">
              Condition {orIndex + 1} - {andIndex + 1}
            </p>
            <span className="btn-icon" onClick={() => onRemoveVisibleLogic(orIndex, andIndex)}>
              <MinusIcon className="w-4 h-4" />
            </span>
          </div>
          <div className="grid gap-x-2 grid-cols-2">
            {Object.keys(inputs).map((key) => {
              const elementKey = `${index}-${fieldKey}-${key}`
              return (
                <div className={`mb-4 col-span-${inputs[key].span}`} key={elementKey}>
                  <RenderInput
                    input={{
                      ...inputs[key],
                      error: error && inputs[key].value === '' ? 'This field is required' : '',
                    }}
                    Key={elementKey}
                    onChange={(_key: string, value: string) => onUpdateVisibleLogic(key, value, inputs[key])}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )
    }

    const renderVisibleLogicAND = (logics: IChecklistVisibilityOption[], orIndex: number) => {
      return (
        <div className={`${orIndex % 2 == 0 ? `bg-slate-100` : ''} p-2`}>
          <div className="flex justify-between items-center">
            <p className="font-bold text-sm">{orIndex + 1}. Visible Conditions (AND)</p>
            <div className="flex">
              <span className="btn-icon" onClick={() => onRemoveVisibleLogic(orIndex)}>
                <MinusIcon className="w-4 h-4" />
              </span>
              <span className="btn-icon" onClick={() => onAddVisibleLogic(orIndex)}>
                <PlusIcon className="w-4 h-4" />
              </span>
            </div>
          </div>

          {logics.map((v, andIndex) => (
            <>
              {renderVisibleLogic(v, orIndex, andIndex)}
              {andIndex < logics.length - 1 && (
                <div className="flex justify-center">
                  <PlusIcon className="w-6 h-6" />
                </div>
              )}
            </>
          ))}
        </div>
      )
    }

    return (
      <div>
        <div className="flex justify-between items-center">
          <div className="flex justify-center items-center gap-2">
            <p className="font-bold text-sm">Visible Conditions (OR)</p>
          </div>
          <span className="btn-icon" onClick={() => onAddVisibleLogic(-1)}>
            <PlusIcon className="w-4 h-4" />
          </span>
        </div>

        {!!control.visibleLogic && control.visibleLogic.map((v, logicIdx) => renderVisibleLogicAND(v, logicIdx))}
      </div>
    )
  }

  const isSelectedAll = useMemo(() => {
    if (!selectedTemplates) return false
    return templates.filter((item) => selectedTemplates.indexOf(item.no) === -1).length == 0
  }, [selectedTemplates, templates])

  return (
    <div className="Conditions-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Conditions - {!defaults || isDuplicated ? 'Add' : `Update No ${defaults.displayNo}`}
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-2" /> <p>Return to Conditions</p>
        </div>
      </Button>

      <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]
          if (input.visible === false) return null
          if (key == 'type') {
            input.additionalElements = (
              <span className="btn-link" onClick={() => setTypeModal(SettingKey.CONDITION_TYPE_OPTIONS)}>
                Update Types
              </span>
            )
            ;(input as any).options = getOptionValues(conditionTypeOptions)
          }
          if (key == 'documentCategory') {
            input.additionalElements = (
              <span className="btn-link" onClick={() => setTypeModal(SettingKey.DOCUMENT_CATEGORY_OPTIONS)}>
                Update Types
              </span>
            )
            ;(input as any).options = getOptionValues(documentCategoryOptions)
          }
          if (key === 'showExpirationDates' && !inputs.checklistEnabled?.value) return

          return (
            <div className={`input ${input.span ? `md:col-span-${input.span}` : ''}`} key={index}>
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>

      {inputs.checklistEnabled?.value && (
        <>
          <div className="grid grid-cols-2 gap-4">
            {checklist?.map((item, index: number) => {
              let rtl: any[] = []

              rtl.push(
                <div key={`checklist-header-${index}`} className="col-span-2 mb-2">
                  <div className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center py-2`}>
                    <p className="font-semibold">Checklist Item {index + 1}</p>
                    <div className="flex items-center gap-2">
                      <p className="btn-icon" onClick={() => onMoveChecklist(index, -1)}>
                        <ArrowUpIcon className="w-4 h-4" />
                      </p>
                      <p className="btn-icon" onClick={() => onMoveChecklist(index, 1)}>
                        <ArrowDownIcon className="w-4 h-4" />
                      </p>
                      <p
                        className="text-sm cursor-pointer text-red-500 hover:underline"
                        onClick={() => onRemoveChecklist(index)}
                      >
                        Remove
                      </p>
                    </div>
                  </div>
                </div>,
              )

              rtl.push(<div key={`checklist-visible-${index}`}>{renderVisibleLogicOR(item, index)}</div>)
              const temp = Object.keys(defaultChecklistInputs()).map((key) => {
                const input = cloneDeep(defaultChecklistInputs()[key])
                input.value = (item as any)[key]

                return (
                  <div className={`col-span-${input.span} mt-2`} key={`${index}${key}`}>
                    <RenderInput
                      input={input}
                      Key={`${index}-${key}`}
                      onChange={(_key: string, value: any) => onChangeChecklist(index, key, value)}
                    />
                  </div>
                )
              })

              if (item.checkIneligible) {
                temp.push(
                  <div key={`${index}eligibleValue`} className="mt-2">
                    <RenderInput
                      input={
                        {
                          inputType: item.inputType,
                          title: 'Eligible Value',
                          value: item.eligibleValue,
                        } as any
                      }
                      Key={`${index}-eligibleValue`}
                      onChange={(_key: string, value: any) => onChangeChecklist(index, 'eligibleValue', value)}
                    />
                  </div>,
                )
              }
              rtl = [...rtl, <div key={`checklist-inputs-${index}`}>{temp}</div>]
              return rtl
            })}
          </div>
          <div className="my-4">
            <span className="text-shade-blue cursor-pointer hover:underline" onClick={onAddChecklist}>
              + Add Checklist Item
            </span>
          </div>
        </>
      )}

      <div className="mb-4">
        <div className="flex items-center gap-4 mb-3 border-b">
          <h2 className="text-md font-bold flex items-center">Templates</h2>

          <Toggle
            id={`template-all`}
            title={`Select All`}
            key={`${lastUpdatedAt}-template-check-all`}
            value={isSelectedAll}
            onChange={() => setSelectedTemplates(isSelectedAll ? [] : templates.map((v) => v.no))}
          />
        </div>
        <div className="grid md:grid-cols-2 mb-5 gap-3">
          {selectedTemplates &&
            templates.map((item, index: number) => (
              <div className="col-12 col-md-6" key={`${lastUpdatedAt}-template-${index}`}>
                <Toggle
                  id={`${item.no}`}
                  title={`${item.no} - ${item.name}`}
                  key={`${lastUpdatedAt}-template-check-${index}`}
                  value={selectedTemplates.indexOf(item.no) !== -1}
                  onChange={(value) => changeTempConds(item.no, value)}
                />
              </div>
            ))}
        </div>
        <Checkbox
          id="initializeForActiveLoan"
          title="We need to initialize for all active loans"
          value={isForActiveLoan}
          onChange={(v) => setForActiveLoan(v)}
        />
      </div>

      <div className="block text-center">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>{!defaults || isDuplicated ? 'Add' : 'Update'} Condition</>
        </Button>
      </div>

      {typeSettingModal && (
        <OptionModal
          title={`Update ${
            typeSettingModal == SettingKey.CONDITION_TYPE_OPTIONS ? 'Condition Type' : 'Document Category'
          } options`}
          settingKey={typeSettingModal}
          onClose={(values: IOptionType[]) => {
            values &&
              dispatch(
                setGlobalConfig(
                  typeSettingModal == SettingKey.CONDITION_TYPE_OPTIONS
                    ? GlobalConfigType.ConditionTypes
                    : GlobalConfigType.DocumentCategory,
                  values,
                ),
              )
            setTypeModal(null)
          }}
        />
      )}
    </div>
  )
}
